import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import { getRecordingsAPI, deleteRecordingAPI } from '../../../context-api/api-services/twilio.service';
import { TH_SX, TD_SX } from './StatusLogsTable';
import { RecordingAudioPlayer } from './RecordingAudioPlayer';
import { RecordingTranscript } from './RecordingTranscript';
import { fmtRecDuration, fmtRecDate, shouldShowTooltip } from '../utils/marketingLeadHelpers';

const TRANSCRIPT_PREVIEW_LENGTH = 48;

export function CallRecordingPanel({ phone, callSid, customerName, canDeleteRecording = true }) {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [expanded_recording_sid, setExpandedRecordingSid] = useState(null);
  const [playing_recording_sid, setPlayingRecordingSid] = useState(null);
  const audio_ref = useRef(null);

  const seekToTranscriptTime = (seconds) => {
    const audio = audio_ref.current;
    if (!audio || seconds == null) return;
    audio.currentTime = seconds;
    audio.play().catch(() => {});
  };

  const toggleTranscriptPanel = (sid) => {
    if (expanded_recording_sid === sid) {
      setExpandedRecordingSid(null);
      setPlayingRecordingSid(null);
      return;
    }
    setExpandedRecordingSid(sid);
    setPlayingRecordingSid(null);
  };

  const togglePlay = (sid) => {
    if (expanded_recording_sid === sid && playing_recording_sid === sid) {
      setExpandedRecordingSid(null);
      setPlayingRecordingSid(null);
      return;
    }
    setExpandedRecordingSid(sid);
    setPlayingRecordingSid(sid);
  };

  const fetchRecordings = React.useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    setExpandedRecordingSid(null);
    setPlayingRecordingSid(null);
    const params = { page: p, limit: 10 };
    // if (phone) params.phone = phone;
    if (phone) params.phone = '+17739975315';
    else if (callSid) params.call_sid = callSid;

    const res = await getRecordingsAPI(params);

    if (res?.hasError) {
      setError(res.message || 'Failed to load recordings.');
      setLoading(false);
      return;
    }

    const payload = res?.data ?? res;
    const items = Array.isArray(payload?.data) ? payload.data
      : Array.isArray(payload) ? payload : [];
    setRecordings(items);
    setPagination(payload?.pagination ?? null);
    setLoading(false);
  }, [callSid, phone]);

  useEffect(() => { fetchRecordings(page); }, [fetchRecordings, page]);

  const handleDelete = async (sid) => {
    if (!canDeleteRecording) return;
    if (!window.confirm('Delete this recording? This cannot be undone.')) return;
    setDeleting(sid);
    const res = await deleteRecordingAPI(sid);
    setDeleting(null);
    if (!res?.hasError) {
      setRecordings((prev) => prev.filter((r) => (r.recording_sid || r.sid) !== sid));
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 5, textAlign: 'center' }}>
        <CircularProgress size={22} sx={{ color: '#111111' }} />
        <Typography sx={{ mt: 1.5, fontFamily: 'Inter Regular, sans-serif', fontSize: 13, color: '#9CA3AF' }}>
          Loading recordings…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 13, color: '#DC2626' }}>
          {error}
        </Typography>
        <Button
          size='small'
          onClick={() => fetchRecordings(page)}
          sx={{ mt: 1, textTransform: 'none', fontSize: 12, color: '#111111', fontFamily: 'Inter Bold, sans-serif' }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (recordings.length === 0) {
    return (
      <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 52, height: 52, borderRadius: '14px',
            bgcolor: '#F3F4F6', border: '1px solid #E8E8E8',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <PhoneInTalkRoundedIcon sx={{ fontSize: 24, color: '#C0C0C0' }} />
        </Box>
        <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 13.5, color: '#374151' }}>
          No call recordings
        </Typography>
        <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12.5, color: '#9CA3AF', textAlign: 'center', maxWidth: 280 }}>
          Call recordings will appear here once available for this lead.
        </Typography>
      </Box>
    );
  }

  const total_pages = pagination?.total_pages ?? 1;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box
        sx={{
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1.5px solid #D1D5DB',
          boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
        }}
      >
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size='small' sx={{ minWidth: 620, borderCollapse: 'collapse' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...TH_SX, width: 36, textAlign: 'center' }}>#</TableCell>
                <TableCell sx={TH_SX}>User</TableCell>
                <TableCell sx={TH_SX}>Transcript</TableCell>
                <TableCell sx={TH_SX}>Duration</TableCell>
                <TableCell sx={{ ...TH_SX, textAlign: 'right' }}>Date</TableCell>
                <TableCell sx={{ ...TH_SX, textAlign: 'center' }}>Play</TableCell>
                <TableCell sx={{ ...TH_SX, textAlign: 'center' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recordings.map((rec, idx) => {
                const sid = rec.recording_sid || rec.sid;
                const is_completed = String(rec.status || '').toLowerCase() === 'completed';
                const transcription = rec.transcription || {};
                const transcript_status = transcription.transcription_status || 'none';
                const transcript_text = transcription.transcription_text || '';
                const is_expanded = expanded_recording_sid === sid;
                const is_playing = playing_recording_sid === sid;
                const is_long_transcript = transcript_text.length > TRANSCRIPT_PREVIEW_LENGTH;
                const user_label =
                  rec.caller?.name ||
                  rec.agent_name ||
                  rec.changed_by_name ||
                  rec.identity ||
                  '—';
                const role_label =
                  rec.caller?.role ||
                  rec.agent_role ||
                  rec.changed_by_role;
                const user_display = `${user_label}${role_label ? ` (${String(role_label).replace(/_/g, ' ')})` : ''}`;

                return (
                  <React.Fragment key={sid}>
                    <TableRow
                      sx={{
                        bgcolor: idx % 2 === 0 ? '#FFFFFF' : '#F9FAFB',
                        '&:last-child td, &:last-child th': { borderBottom: 'none' },
                        transition: 'background 0.12s',
                        '&:hover td': { bgcolor: '#EEF0F3' },
                      }}
                    >
                      {/* # */}
                      <TableCell sx={{ ...TD_SX, textAlign: 'center', color: '#C0C0C0', width: 36 }}>
                        {(page - 1) * 10 + idx + 1}
                      </TableCell>

                      {/* User */}
                      <TableCell sx={{ ...TD_SX, minWidth: 120, width: 160, whiteSpace: 'nowrap' }}>
                        <Tooltip
                          title={user_display}
                          placement='top'
                          arrow
                          disableHoverListener={!shouldShowTooltip(user_display, 30)}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Inter Bold, sans-serif',
                              fontWeight: 700,
                              fontSize: 13,
                              color: '#111111',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {user_label}
                            {role_label ? ` (${String(role_label).replace(/_/g, ' ')})` : ''}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      {/* Transcript */}
                      <TableCell sx={{ ...TD_SX, maxWidth: 220 }}>
                        {transcript_status === 'completed' && transcript_text ? (
                          is_long_transcript ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontFamily: 'Inter Regular, sans-serif',
                                  fontSize: 12,
                                  color: '#374151',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                {`${transcript_text.slice(0, TRANSCRIPT_PREVIEW_LENGTH)}…`}
                              </Typography>
                              <Tooltip
                                title={is_expanded ? 'Collapse transcript' : 'View full transcript'}
                                placement='top'
                              >
                                <Box
                                  onClick={() => toggleTranscriptPanel(sid)}
                                  sx={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: '6px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    cursor: 'pointer',
                                    bgcolor: is_expanded ? '#111111' : '#F3F4F6',
                                    border: `1px solid ${is_expanded ? '#111111' : '#E5E7EB'}`,
                                    transition: 'all 0.15s',
                                    '&:hover': { bgcolor: is_expanded ? '#333' : '#E5E7EB' },
                                  }}
                                >
                                  {is_expanded
                                    ? <KeyboardArrowUpRoundedIcon sx={{ fontSize: 14, color: '#FFFFFF' }} />
                                    : <KeyboardArrowDownRoundedIcon sx={{ fontSize: 14, color: '#374151' }} />
                                  }
                                </Box>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Typography
                              sx={{
                                fontFamily: 'Inter Regular, sans-serif',
                                fontSize: 12,
                                color: '#374151',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {transcript_text}
                            </Typography>
                          )
                        ) : transcript_status === 'completed' ? (
                          <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 11.5, fontWeight: 600, color: '#059669' }}>
                            Available
                          </Typography>
                        ) : (
                          <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 11.5, color: '#9CA3AF' }}>
                            Not available
                          </Typography>
                        )}
                      </TableCell>

                      {/* Duration */}
                      <TableCell sx={TD_SX}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.3, borderRadius: '6px', bgcolor: '#F3F4F6', border: '1px solid #E8E8E8' }}>
                          <PhoneInTalkRoundedIcon sx={{ fontSize: 11, color: '#9CA3AF' }} />
                          <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 11.5, color: '#374151' }}>
                            {fmtRecDuration(rec.duration)}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Date */}
                      <TableCell sx={{ ...TD_SX, textAlign: 'right', color: '#9CA3AF', fontSize: 12 }}>
                        {fmtRecDate(rec.created_at || rec.date_created)}
                      </TableCell>

                      {/* Play */}
                      <TableCell sx={{ ...TD_SX, textAlign: 'center' }}>
                        {is_completed && sid ? (
                          <Box
                            onClick={() => togglePlay(sid)}
                            title={is_playing ? 'Collapse player' : 'Play recording'}
                            sx={{
                              width: 28, height: 28, borderRadius: '50%',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              cursor: 'pointer',
                              bgcolor: is_playing ? '#111111' : '#F3F4F6',
                              border: `1px solid ${is_playing ? '#111111' : '#E5E7EB'}`,
                              transition: 'all 0.15s',
                              '&:hover': { bgcolor: is_playing ? '#333' : '#E5E7EB' },
                            }}
                          >
                            {is_playing
                              ? <PauseRoundedIcon sx={{ fontSize: 14, color: '#FFFFFF' }} />
                              : <PlayArrowRoundedIcon sx={{ fontSize: 14, color: '#374151' }} />
                            }
                          </Box>
                        ) : (
                          <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 11, color: '#D1D5DB' }}>
                            {rec.status || 'pending'}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Delete */}
                      <TableCell sx={{ ...TD_SX, textAlign: 'center' }}>
                        <Tooltip
                          title={
                            canDeleteRecording
                              ? 'Delete recording'
                              : 'Only regional branch users can delete recordings'
                          }
                          placement='top'
                        >
                          <Box
                            onClick={() => deleting !== sid && canDeleteRecording && handleDelete(sid)}
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor:
                                deleting === sid || !canDeleteRecording ? 'not-allowed' : 'pointer',
                              bgcolor: canDeleteRecording ? '#FEF2F2' : '#F3F4F6',
                              border: canDeleteRecording ? '1px solid #FECACA' : '1px solid #E5E7EB',
                              opacity: deleting === sid || !canDeleteRecording ? 0.5 : 1,
                              transition: 'all 0.15s',
                              '&:hover': {
                                bgcolor:
                                  deleting === sid || !canDeleteRecording ? '#F3F4F6' : '#FEE2E2',
                                borderColor:
                                  deleting === sid || !canDeleteRecording ? '#E5E7EB' : '#FCA5A5',
                              },
                            }}
                          >
                            <DeleteOutlineRoundedIcon
                              sx={{ fontSize: 13, color: canDeleteRecording ? '#DC2626' : '#9CA3AF' }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {/* Expandable recording panel */}
                    {is_expanded && (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          sx={{
                            p: 0,
                            borderBottom: '1px solid #E5E7EB',
                            bgcolor: '#F9FAFB',
                            overflow: 'hidden',
                            maxWidth: 0,
                          }}
                        >
                          <Box sx={{ px: 2, py: 1.5, minWidth: 0, overflow: 'hidden' }}>
                            <RecordingAudioPlayer
                              ref={audio_ref}
                              recording_sid={sid}
                              is_active={is_playing}
                              on_play={setPlayingRecordingSid}
                            />
                            <RecordingTranscript
                              recording_sid={sid}
                              initial_transcription={transcription}
                              customer_name={customerName}
                              on_seek={seekToTranscriptTime}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Pagination */}
      {total_pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: '#9CA3AF' }}>
            Page {page} of {total_pages}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size='small'
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              sx={{ textTransform: 'none', fontSize: 12, color: '#374151', fontFamily: 'Inter Regular, sans-serif', border: '1px solid #E5E7EB', borderRadius: '7px', minWidth: 0, px: 1.5 }}
            >
              ← Prev
            </Button>
            <Button
              size='small'
              disabled={page >= total_pages}
              onClick={() => setPage((p) => p + 1)}
              sx={{ textTransform: 'none', fontSize: 12, color: '#374151', fontFamily: 'Inter Regular, sans-serif', border: '1px solid #E5E7EB', borderRadius: '7px', minWidth: 0, px: 1.5 }}
            >
              Next →
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
